/**
 * Query key factory for React Query
 * Provides type-safe, consistent query keys for caching and invalidation
 */

type GetQueryKeyReturnType<T> = T extends {} ? [...string[], T] : string[];

const getQueryKey = <T>(keys: string[], params?: T): GetQueryKeyReturnType<T> => {
  if (params !== undefined && params !== null) {
    return [...keys, params] as GetQueryKeyReturnType<T>;
  }

  return keys as GetQueryKeyReturnType<T>;
};

export const queryKeys = {
  users: {
    all: ['users'],
    current: () => getQueryKey([...queryKeys.users.all, 'current']),
    byId: (id: string) => getQueryKey([...queryKeys.users.all, id]),
  },
  organization: {
    all: ['organization'],
    current: () => getQueryKey([...queryKeys.organization.all, 'current']),
  },
  settings: {
    all: ['settings'],
    attributes: <T>(params?: T) => getQueryKey([...queryKeys.settings.all, 'attributes'], params),
    employees: <T>(params?: T) => getQueryKey([...queryKeys.settings.all, 'employees'], params),
    jobs: <T>(params?: T) => getQueryKey([...queryKeys.settings.all, 'jobs'], params),
    tasks: <T>(params?: T) => getQueryKey([...queryKeys.settings.all, 'tasks'], params),
    regulations: <T>(params?: T) => getQueryKey([...queryKeys.settings.all, 'regulations'], params),
    training: <T>(params?: T) => getQueryKey([...queryKeys.settings.all, 'training'], params),
    groups: <T>(params?: T) => getQueryKey([...queryKeys.settings.all, 'groups'], params),
  },
  profile: {
    all: ['profile'],
    attributes: (employeeId: string, params?: Record<string, unknown>) =>
      getQueryKey([...queryKeys.profile.all, employeeId, 'attributes'], params),
    jobAttributes: (employeeId: string, params?: Record<string, unknown>) =>
      getQueryKey([...queryKeys.profile.all, employeeId, 'jobAttributes'], params),
  },
  team: {
    all: ['team'],
    data: (params?: Record<string, unknown>) =>
      getQueryKey([...queryKeys.team.all, 'data'], params),
    attributes: (params?: Record<string, unknown>) =>
      getQueryKey([...queryKeys.team.all, 'attributes'], params),
    gaps: (params?: Record<string, unknown>) =>
      getQueryKey([...queryKeys.team.all, 'gaps'], params),
  },
  dashboard: {
    all: ['dashboard'],
    data: () => getQueryKey([...queryKeys.dashboard.all, 'data']),
    notifications: () => getQueryKey([...queryKeys.dashboard.all, 'notifications']),
  },
  billing: {
    all: ['billing'],
    plans: () => getQueryKey([...queryKeys.billing.all, 'plans']),
    invoices: () => getQueryKey([...queryKeys.billing.all, 'invoices']),
    settings: () => getQueryKey([...queryKeys.billing.all, 'settings']),
  },
  groups: {
    all: ['groups'],
    list: () => getQueryKey([...queryKeys.groups.all, 'list']),
    byId: (groupId: string) => getQueryKey([...queryKeys.groups.all, groupId]),
  },
  locale: {
    all: ['locale'],
    terminology: () => getQueryKey([...queryKeys.locale.all, 'terminology']),
    terminologyHash: () => getQueryKey([...queryKeys.locale.all, 'terminology', 'hash']),
  },
  employeeSkills: {
    key: ['employee_skills'],
    list: <T>(params?: T) => getQueryKey([...queryKeys.employeeSkills.key, 'list'], params),
    byId: (employeeSkillId: string) =>
      getQueryKey([...queryKeys.employeeSkills.key, 'by_id'], employeeSkillId),
    categories: () => getQueryKey([...queryKeys.employeeSkills.key, 'categories']),
  },
  careers: {
    key: ['careers'],
    list: <T>(params?: T) => getQueryKey([...queryKeys.careers.key, 'list'], params),
    byId: (careerId: string) => getQueryKey([...queryKeys.careers.key, 'by_id'], careerId),
    goal: () => getQueryKey([...queryKeys.careers.key, 'goal']),
    learningPathway: () => getQueryKey([...queryKeys.careers.key, 'learning-pathway']),
    benchmark: (careerId: string) => getQueryKey([...queryKeys.careers.key, 'benchmark', careerId]),
  },
  courses: {
    key: ['courses'],
    list: <T>(params?: T) => getQueryKey([...queryKeys.courses.key, 'list'], params),
  },
  trainingBalance: {
    key: ['training-balance'],
    balance: () => getQueryKey([...queryKeys.trainingBalance.key, 'balance']),
  },
  feedbackCourseRequests: {
    key: ['feedback-course-requests'],
    list: <T>(params?: T) => getQueryKey([...queryKeys.feedbackCourseRequests.key, 'list'], params),
  },
  courseStudents: {
    key: ['course-students'],
    list: <T>(params?: T) => getQueryKey([...queryKeys.courseStudents.key, 'list'], params),
    summary: <T>(courseId?: string, params?: T) =>
      getQueryKey([...queryKeys.courseStudents.key, 'summary'], { courseId, ...params }),
  },
  studentCourses: {
    key: ['student-courses'],
    list: <T>(params?: T) => getQueryKey([...queryKeys.studentCourses.key, 'list'], params),
  },
  trainingStudents: {
    key: ['training-students'],
    list: <T>(params?: T) => getQueryKey([...queryKeys.trainingStudents.key, 'list'], params),
    summary: <T>(params?: T) => getQueryKey([...queryKeys.trainingStudents.key, 'summary'], params),
  },
  trainingCourses: {
    key: ['training-course'],
    list: <T>(params?: T) => getQueryKey([...queryKeys.trainingCourses.key, 'list'], params),
    byId: (courseId?: string) => getQueryKey([...queryKeys.trainingCourses.key, 'by_id'], courseId),
  },
  explore: {
    key: ['explore'],
    solutions: () => getQueryKey([...queryKeys.explore.key, 'solutions']),
    problems: () => getQueryKey([...queryKeys.explore.key, 'problems']),
    lookup: () => getQueryKey([...queryKeys.explore.key, 'lookup']),
    search: (query: string) => getQueryKey([...queryKeys.explore.key, 'search', query]),
  },
  enrollableStudents: {
    key: ['enrollable-students'],
    list: (params?: Record<string, unknown>) =>
      getQueryKey([...queryKeys.enrollableStudents.key, 'list'], params),
  },
  enrolmentPreview: {
    key: ['enrolment-preview'],
    pairs: (params?: Record<string, unknown>) =>
      getQueryKey([...queryKeys.enrolmentPreview.key, 'pairs'], params),
  },
  countries: {
    key: ['countries'],
    supported: () => getQueryKey([...queryKeys.countries.key, 'supported']),
    reference: () => getQueryKey([...queryKeys.countries.key, 'reference']),
  },
};
